import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, BadgeCheck, ClipboardCheck, Factory, FlaskConical, PackageCheck, ShieldCheck, Ship } from "lucide-react";
import { Seo } from "@/components/Seo";
import { MaskedLines, Overline, Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { CtaBand } from "@/components/CtaBand";
import { WhatsAppButton } from "@/components/WhatsAppButton";

const trustPoints = [
    "Verified partner processor sourcing across Rajasthan",
    "Batch-wise COA and lab test support",
    "FOB / CFR / CIF shipment options",
    "Shipments through suitable Indian ports",
    "Direct co-founder communication on every order",
];

const marqueeItems = [
    "Crude Protein 36–38% Min",
    "50 kg PP/HDPE Export Bags",
    "22–24 MT per 20ft Container",
    "FOB · CFR · CIF Terms",
    "Batch-wise COA Support",
    "Mundra · Kandla · JNPT · Pipavav · Hazira",
];

const specCards = [
    { label: "Crude Protein", value: "36–38%", note: "Minimum typical" },
    { label: "Moisture", value: "10–12%", note: "Maximum typical" },
    { label: "Sand & Silica", value: "≤ 2%", note: "Maximum" },
    { label: "Packing", value: "50 kg", note: "PP/HDPE export bags" },
    { label: "Container Load", value: "22–24 MT", note: "Per 20ft container" },
    { label: "Origin", value: "Rajasthan", note: "Partner processor network, India" },
];

const qualityPoints = [
    { icon: ShieldCheck, title: "Batch-wise COA coordination", text: "A Certificate of Analysis is coordinated for every batch before dispatch." },
    { icon: FlaskConical, title: "Lab test support", text: "Protein, moisture, oil/fat, and sand & silica verified through lab testing on request." },
    { icon: ClipboardCheck, title: "Third-party inspection", text: "SGS, Geo-Chem, or equivalent inspection arranged at loading on buyer request." },
    { icon: Factory, title: "Verified Rajasthan partner processors", text: "Sourcing only from vetted processors across Rajasthan's oilseed belt, incl. Niwai." },
    { icon: PackageCheck, title: "Export packing & stuffing coordination", text: "50 kg PP/HDPE bags stuffed at 22–24 MT per 20ft container under supervision." },
    { icon: Ship, title: "FOB / CFR / CIF shipment support", text: "Flexible terms through Mundra, Kandla, JNPT/Nhava Sheva, Pipavav, and Hazira." },
];

const fadeUp = (delay = 0) => ({
    initial: { opacity: 0, y: 24 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] },
});

export default function Home() {
    const heroRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], [0, 70]);

    return (
        <>
            <Seo
                title="Rapeseed/Mustard DOC Exporter from India | AXORA PS GLOBAL LLP"
                description="Merchant export of quality-verified rapeseed/mustard de-oiled cake (DOC) from India for global feed mills, commodity importers, and trading companies. Protein 36–38%, batch-wise COA, FOB/CFR/CIF."
                path="/"
                ogImage="/images/og-home.jpg"
            />

            <section ref={heroRef} data-testid="home-hero" className="relative overflow-hidden bg-white pt-[72px]">
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-x-0 top-0 h-[560px] bg-gradient-to-b from-steel-light/80 via-steel-light/30 to-white"
                />
                <div className="relative mx-auto grid w-full max-w-7xl grid-cols-1 gap-14 px-5 pb-20 pt-14 sm:px-8 lg:grid-cols-12 lg:gap-10 lg:px-12 lg:pb-28 lg:pt-20">
                    <div className="lg:col-span-7">
                        <motion.div {...fadeUp(0)} className="flex items-center gap-3.5">
                            <img src="/images/logo.png" alt="AXORA PS GLOBAL LLP logo" className="h-12 w-12 object-contain" />
                            <Overline>AXORA PS GLOBAL LLP — Merchant Exporter, Jaipur, India</Overline>
                        </motion.div>
                        <MaskedLines
                            lines={["Rapeseed/Mustard DOC", "Exporter from India."]}
                            className="mt-5 font-display text-4xl font-bold leading-[1.06] tracking-tight text-navy sm:text-5xl lg:text-6xl"
                        />
                        <motion.p {...fadeUp(0.55)} className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 md:text-lg">
                            Merchant export of quality-verified rapeseed/mustard de-oiled cake from India for global
                            feed mills, commodity importers, and trading companies.
                        </motion.p>
                        <motion.div {...fadeUp(0.7)} className="mt-9 flex flex-wrap items-center gap-4">
                            <WhatsAppButton label="Request Quote on WhatsApp" location="hero" className="px-7 py-3.5 text-base" />
                            <Link
                                to="/products/rapeseed-mustard-doc"
                                data-testid="hero-view-specs-button"
                                className="inline-flex items-center gap-2 rounded-md border border-navy/25 bg-white px-7 py-3.5 text-base font-semibold text-navy transition-[background-color,color,border-color,transform] duration-300 hover:-translate-y-0.5 hover:border-navy hover:bg-navy hover:text-white"
                            >
                                View Product Specs
                                <ArrowRight className="h-4 w-4" />
                            </Link>
                        </motion.div>
                        <ul className="mt-12 grid max-w-xl grid-cols-1 gap-x-8 gap-y-3.5 sm:grid-cols-2" data-testid="hero-trust-points">
                            {trustPoints.map((point, i) => (
                                <motion.li key={point} {...fadeUp(0.85 + i * 0.08)} className="flex items-start gap-2.5 text-sm font-medium text-navy/85">
                                    <BadgeCheck className="mt-0.5 h-4 w-4 shrink-0 text-steel" />
                                    {point}
                                </motion.li>
                            ))}
                        </ul>
                    </div>
                    <div className="relative lg:col-span-5">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.96, y: 30 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            transition={{ duration: 1.1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
                            className="relative"
                        >
                            <div aria-hidden="true" className="absolute -right-5 -top-5 hidden h-full w-full rounded-2xl bg-navy sm:block" />
                            <motion.div style={{ y: imgY }} className="relative">
                                <img
                                    src="/images/doc-own-bag.jpg"
                                    alt="Open 50 kg export bag filled with mustard de-oiled cake — the actual DOC supplied by AXORA PS GLOBAL LLP"
                                    className="relative aspect-[4/5] w-full rounded-2xl border border-slate-200 object-cover shadow-[0_30px_60px_rgba(10,37,64,0.25)]"
                                    fetchPriority="high"
                                />
                                <div
                                    data-testid="hero-protein-chip"
                                    className="absolute -left-4 bottom-8 rounded-xl border border-slate-200 bg-white/95 px-5 py-4 shadow-xl backdrop-blur sm:-left-8"
                                >
                                    <p className="font-display text-2xl font-bold tracking-tight text-navy">36–38%</p>
                                    <p className="mt-0.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                                        Crude Protein — min. typical
                                    </p>
                                </div>
                            </motion.div>
                        </motion.div>
                    </div>
                </div>
            </section>

            <Marquee items={marqueeItems} />

            <section data-testid="corporate-profile" className="bg-white py-24 lg:py-32">
                <div className="mx-auto grid w-full max-w-7xl grid-cols-1 gap-12 px-5 sm:px-8 lg:grid-cols-12 lg:gap-16 lg:px-12">
                    <div className="lg:col-span-5">
                        <Reveal>
                            <Overline>Corporate Profile</Overline>
                            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-navy md:text-4xl">
                                Dependable Agri-Commodity Merchant Exporter from Rajasthan
                            </h2>
                        </Reveal>
                        <Reveal delay={0.15} className="mt-10">
                            <div className="relative">
                                <div aria-hidden="true" className="absolute -left-4 -top-4 h-full w-full rounded-2xl border border-steel/40" />
                                <img
                                    src="/images/silos.jpg"
                                    alt="Industrial storage silos at an agri-commodity processing facility in India"
                                    loading="lazy"
                                    className="relative aspect-[4/3] w-full rounded-2xl object-cover"
                                />
                            </div>
                        </Reveal>
                    </div>
                    <div className="flex flex-col justify-center lg:col-span-7">
                        <Reveal>
                            <p className="text-base leading-relaxed text-slate-600 md:text-lg">
                                AXORA PS GLOBAL LLP is a Jaipur-based merchant exporter specializing in dependable
                                agri-commodity exports from India, sourcing export-suitable Rapeseed/Mustard DOC from
                                verified partner processors in Rajasthan.
                            </p>
                            <p className="mt-5 text-base leading-relaxed text-slate-600">
                                The company supports international buyers with sourcing, quality coordination,
                                batch-wise COA support, export documentation, container stuffing coordination, and
                                shipment through suitable Indian ports — with direct co-founder communication at every
                                stage of the order.
                            </p>
                        </Reveal>
                        <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-3">
                            {[
                                { value: "GST + IEC", label: "Registered & verified merchant exporter LLP" },
                                { value: "5 Ports", label: "Mundra, Kandla, JNPT, Pipavav, Hazira" },
                                { value: "100%", label: "Batch-wise COA and lab test support" },
                            ].map((stat, i) => (
                                <Reveal key={stat.value} delay={0.1 + i * 0.1}>
                                    <div className="rounded-xl border border-slate-200 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(10,37,64,0.1)]">
                                        <p className="font-display text-2xl font-bold tracking-tight text-navy">{stat.value}</p>
                                        <p className="mt-1.5 text-xs leading-relaxed text-slate-500">{stat.label}</p>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <section data-testid="quality-trust" className="border-t border-slate-200 bg-white py-24 lg:py-32">
                <div className="mx-auto w-full max-w-7xl px-5 sm:px-8 lg:px-12">
                    <Reveal className="max-w-2xl">
                        <Overline>Product Trust</Overline>
                        <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-navy md:text-4xl">
                            Quality-Verified DOC for Feed Buyers
                        </h2>
                        <p className="mt-4 text-base leading-relaxed text-slate-600">
                            Every lot passes the same verification routine before it is offered for shipment — so the
                            specification you contract is the specification that lands.
                        </p>
                    </Reveal>
                    <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                        {qualityPoints.map((point, i) => (
                            <Reveal key={point.title} delay={i * 0.06}>
                                <div
                                    data-testid={`quality-point-${point.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                    className="flex h-full items-start gap-4 rounded-xl border border-slate-200 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:border-steel/50 hover:shadow-[0_16px_40px_rgba(10,37,64,0.1)]"
                                >
                                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-steel-light">
                                        <point.icon className="h-5 w-5 text-navy" />
                                    </span>
                                    <div>
                                        <h3 className="font-display text-base font-bold tracking-tight text-navy">{point.title}</h3>
                                        <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{point.text}</p>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section data-testid="featured-product" className="bg-mist py-24 lg:py-32">
                <div className="mx-auto w-full max-w-7xl px-5 sm:px-8 lg:px-12">
                    <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
                        <Reveal>
                            <Overline>Featured Product</Overline>
                            <h2 className="mt-4 max-w-xl font-display text-3xl font-bold tracking-tight text-navy md:text-4xl">
                                Rapeseed/Mustard De-Oiled Cake (DOC)
                            </h2>
                            <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600">
                                High-protein animal feed raw material, also traded as Rapeseed-Mustard Meal, Mustard
                                DOC, Rapeseed DOC, and Mustard/Rapeseed Oil Cake.
                            </p>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <Link
                                to="/products/rapeseed-mustard-doc"
                                data-testid="featured-full-specs-link"
                                className="inline-flex items-center gap-2 font-display text-sm font-bold uppercase tracking-[0.16em] text-steel transition-colors hover:text-navy"
                            >
                                Full Specifications
                                <ArrowRight className="h-4 w-4" />
                            </Link>
                        </Reveal>
                    </div>
                    <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                        {specCards.map((card, i) => (
                            <Reveal key={card.label} delay={i * 0.07}>
                                <div
                                    data-testid={`spec-card-${card.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                    className="h-full rounded-xl border border-slate-200 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-steel/50 hover:shadow-[0_16px_40px_rgba(10,37,64,0.1)]"
                                >
                                    <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">{card.label}</p>
                                    <p className="mt-3 font-display text-3xl font-bold tracking-tight text-navy">{card.value}</p>
                                    <p className="mt-1.5 text-sm text-slate-500">{card.note}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.1} className="mt-14">
                        <div className="grid grid-cols-1 overflow-hidden rounded-2xl border border-slate-200 bg-white lg:grid-cols-2">
                            <img
                                src="/images/bags-truck.jpg"
                                alt="Truck loaded with 50 kg export bags of rapeseed/mustard DOC preparing for dispatch to an Indian port"
                                loading="lazy"
                                className="h-full min-h-[260px] w-full object-cover"
                            />
                            <div className="flex flex-col justify-center p-8 lg:p-12">
                                <h3 className="font-display text-2xl font-bold tracking-tight text-navy">
                                    Sourced from verified partner processors
                                </h3>
                                <p className="mt-4 text-base leading-relaxed text-slate-600">
                                    Export-suitable DOC is sourced across Rajasthan's partner processor network,
                                    including the Niwai sourcing hub, and shipped through Mundra, Kandla/Deendayal,
                                    JNPT/Nhava Sheva, Pipavav, and Hazira on FOB, CFR, or CIF terms.
                                </p>
                                <div className="mt-8 flex flex-wrap gap-4">
                                    <WhatsAppButton label="Request Quote on WhatsApp" location="featured-product" />
                                    <Link
                                        to="/products"
                                        data-testid="featured-all-products-link"
                                        className="inline-flex items-center gap-2 rounded-md border border-navy/25 px-5 py-2.5 text-sm font-semibold text-navy transition-colors duration-300 hover:border-navy hover:bg-navy hover:text-white"
                                    >
                                        All Products
                                        <ArrowRight className="h-4 w-4" />
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            <CtaBand location="home" />
        </>
    );
}
